import * as z from "zod";
import { changedTeamPatchPaths } from "./grouped-settings-changes";
import {
  diffSettingsGroups,
  mergeSettingsGroups,
  type SettingsGroups,
} from "./tab-settings/settings-group-diff";

export const MERCHANT_MAX_LABEL_PRICE_MIN = 1;
export const MERCHANT_MAX_LABEL_PRICE_MAX = 2000;

export const ReturnsPoliciesSettingsSchema = z.object({
  workflows: z.object({
    /** Charged on top of the label price. Null when the merchant charges none. */
    customerLabelFee: z.number().nullable(),
    waiveLabelFeeWhenOnlyOption: z.boolean(),
    waiveLabelFeeForExchanges: z.boolean(),
    /**
     * USD cap on a merchant-paid label. Null falls back to Redo's default.
     * Bounded here because the handler writes with `updateOne`, which skips
     * the Mongoose min/max on the stored field.
     */
    maxLabelPrice: z
      .number()
      .min(MERCHANT_MAX_LABEL_PRICE_MIN)
      .max(MERCHANT_MAX_LABEL_PRICE_MAX)
      .nullable(),
  }),
});

export type ReturnsPoliciesSettings = z.infer<
  typeof ReturnsPoliciesSettingsSchema
>;

export const ReturnsPoliciesSettingsChangesSchema = z.object({
  workflows: ReturnsPoliciesSettingsSchema.shape.workflows.partial().optional(),
});

export type ReturnsPoliciesSettingsChanges = z.infer<
  typeof ReturnsPoliciesSettingsChangesSchema
>;

export interface ReturnsPoliciesTeamPatch {
  /** Dotted paths relative to `team.settings`. */
  set: Record<string, unknown>;
  unset: string[];
}

const SETTINGS_GROUP_KEYS = ["workflows"];

/** The slice of team settings the Policies tab reads. */
export interface ReturnsPoliciesSettingsSource {
  settings: {
    customerLabelUpchargeFee?: number | null;
    dontChargeCustomerLabelUpchargeWhenOnlyOption?: boolean | null;
    excludeCustomerLabelFeeForExchanges?: boolean | null;
    returns?: { merchantMaxLabelPrice?: number | null } | null;
  };
}

function settingsGroups(settings: ReturnsPoliciesSettings): SettingsGroups {
  return { workflows: settings.workflows };
}

export function returnsPoliciesSettingsFromTeam({
  settings,
}: ReturnsPoliciesSettingsSource): ReturnsPoliciesSettings {
  return {
    workflows: {
      customerLabelFee: settings.customerLabelUpchargeFee ?? null,
      waiveLabelFeeWhenOnlyOption:
        !!settings.dontChargeCustomerLabelUpchargeWhenOnlyOption,
      waiveLabelFeeForExchanges: !!settings.excludeCustomerLabelFeeForExchanges,
      maxLabelPrice: settings.returns?.merchantMaxLabelPrice ?? null,
    },
  };
}

/** Label fees and caps move money, so they stay behind shipping settings. */
export function returnsPoliciesChangesNeedShippingPermission(
  changes: ReturnsPoliciesSettingsChanges,
): boolean {
  return Object.keys(changes.workflows ?? {}).length > 0;
}

export function returnsPoliciesSettingsChanges(
  initial: ReturnsPoliciesSettings,
  next: ReturnsPoliciesSettings,
): ReturnsPoliciesSettingsChanges {
  return ReturnsPoliciesSettingsChangesSchema.parse(
    diffSettingsGroups(
      SETTINGS_GROUP_KEYS,
      settingsGroups(initial),
      settingsGroups(next),
    ),
  );
}

export function returnsPoliciesSettingsToTeamPatch(
  next: ReturnsPoliciesSettings,
): ReturnsPoliciesTeamPatch {
  const {
    customerLabelFee,
    waiveLabelFeeWhenOnlyOption,
    waiveLabelFeeForExchanges,
    maxLabelPrice,
  } = next.workflows;
  const set: Record<string, unknown> = {
    dontChargeCustomerLabelUpchargeWhenOnlyOption: waiveLabelFeeWhenOnlyOption,
    excludeCustomerLabelFeeForExchanges: waiveLabelFeeForExchanges,
  };
  const unset: string[] = [];
  if (customerLabelFee === null) {
    unset.push("customerLabelUpchargeFee");
  } else {
    set.customerLabelUpchargeFee = customerLabelFee;
  }
  if (maxLabelPrice === null) {
    unset.push("returns.merchantMaxLabelPrice");
  } else {
    set["returns.merchantMaxLabelPrice"] = maxLabelPrice;
  }
  return { set, unset };
}

export function returnsPoliciesTeamPatchForChanges(
  current: ReturnsPoliciesSettingsSource,
  changes: ReturnsPoliciesSettingsChanges,
): ReturnsPoliciesTeamPatch {
  const stored = returnsPoliciesSettingsFromTeam(current);
  const baseline = returnsPoliciesSettingsToTeamPatch(stored);
  const requested = returnsPoliciesSettingsToTeamPatch(
    ReturnsPoliciesSettingsSchema.parse(
      mergeSettingsGroups(SETTINGS_GROUP_KEYS, settingsGroups(stored), changes),
    ),
  );

  return changedTeamPatchPaths(baseline, requested);
}
